import { clamp, lerp, arcPosition } from '../../core/math';
import { Timeline } from '../../core/timeline';
import type { BehaviorContext, ThemeBehavior } from '../../core/behavior';
import type { SpriteProp } from '../../core/effects';
import type { Action, Motion, Point, RyukAction, Surface } from '../../core/types';

/** Ryuk owns his choreography, not the simulation loop, DOM or asset loading. */
export class RyukBehavior implements ThemeBehavior {
  private timeline = new Timeline();
  private active: RyukAction | undefined;
  private elapsed = 0;
  private lastTrail = 0;
  private tracked: HTMLElement | undefined;
  private syncScene: (() => void) | undefined;
  private pointer: Point | undefined;

  constructor(private context: BehaviorContext) {}

  canInterrupt(action: Action): boolean {
    return action === 'feed' && this.active === 'beg';
  }

  trigger(action: Action): void {
    this.active = action as RyukAction;
    this.elapsed = 0;
    this.lastTrail = 0;
    switch (action) {
      case 'feed':
        this.feed();
        break;
      case 'juggle':
        this.juggle();
        break;
      case 'fly':
        this.fly();
        break;
      case 'watch':
        this.watch();
        break;
      case 'peek':
        this.peek();
        break;
      case 'beg':
        this.beg();
        break;
      case 'phase':
        this.phase();
        break;
      case 'notebook':
        this.notebook();
        break;
    }
  }

  update(dt: number, pointer?: Point): void {
    this.pointer = pointer;
    if (!this.active) return;
    if (this.tracked && !this.context.page.measureSurface(this.tracked)) {
      this.cancel();
      return;
    }
    this.elapsed += dt;
    this.timeline.update(dt);
    const hero = this.context.hero();
    if (
      (hero.pose === 'fly' || hero.pose === 'phase') &&
      hero.motion &&
      this.elapsed - this.lastTrail > 0.1
    ) {
      this.lastTrail = this.elapsed;
      this.context.effects.afterimage(hero);
      if (hero.pose === 'fly')
        this.context.effects.flecks(hero.x, hero.y - hero.size * 0.55, 'feather', 1);
    }
  }

  reflow(): void {
    this.syncScene?.();
  }

  cancel(): void {
    if (!this.active) return;
    this.context.hero().motion = undefined;
    this.finish();
  }

  private finish = (): void => {
    this.timeline.clear();
    this.active = undefined;
    this.tracked = undefined;
    this.syncScene = undefined;
    this.context.effects.props = [];
    const hero = this.context.hero();
    hero.rotation = 0;
    hero.opacity = 1;
    hero.surfaceOffsetY = 0;
    hero.occlusion = undefined;
    hero.look = undefined;
    const support = this.context.page.measureSurface(hero.surface);
    hero.surface = support?.element;
    hero.y = support?.y ?? this.context.floor();
    this.context.pose(support ? 'perch' : 'idle');
    this.context.complete();
  };

  private apple(frame = 0): SpriteProp {
    const prop: SpriteProp = {
      asset: 'apples',
      frame,
      x: 0,
      y: 0,
      size: this.context.hero().size * 0.13,
      rotation: 0,
      opacity: 1,
      age: 0,
      life: Infinity,
    };
    this.context.effects.props.push(prop);
    return prop;
  }

  private point(x: number, y: number): Point {
    const hero = this.context.hero();
    return { x: hero.x + x * hero.size * hero.facing, y: hero.y + y * hero.size };
  }

  private surfaces(minHeight: number): Surface[] {
    const { hero, page, interacts } = this.context;
    if (!interacts()) return [];
    return page.surfaces.filter(
      (s) =>
        s.width >= hero().size * 0.55 &&
        s.height >= minHeight &&
        s.y > hero().size * 1.05 &&
        s.y + s.height <= this.context.floor() &&
        s.x >= 0 &&
        s.x + s.width <= page.bounds.width,
    );
  }

  private chooseSurface(minHeight = 60): Surface | undefined {
    const choices = this.surfaces(minHeight);
    const other = choices.filter((s) => s.element !== this.context.hero().surface);
    const eligible = other.length ? other : choices;
    return eligible[Math.floor(Math.random() * eligible.length)];
  }

  private travel(x: number, y: number, surface?: Surface, arrive = this.finish): Motion {
    const hero = this.context.hero();
    const motion = this.context.move(this.context.fitX(x), y, true, surface);
    motion.duration = clamp(
      Math.hypot(motion.to.x - motion.from.x, motion.to.y - motion.from.y) / 190,
      0.9,
      2.4,
    );
    motion.arc = Math.min(68, Math.max(18, Math.min(y, hero.y) - hero.size * 1.2));
    motion.quietLanding = true;
    motion.onComplete = arrive;
    this.context.pose('fly');
    return motion;
  }

  private fly(): void {
    // A title can support his feet; hiding and phasing need a taller card.
    const surface = this.chooseSurface(18);
    const hero = this.context.hero();
    const x = surface
      ? surface.x + surface.width / 2
      : this.context.page.bounds.width *
        (hero.x > this.context.page.bounds.width / 2 ? 0.25 : 0.75);
    this.tracked = surface?.element;
    this.travel(x, surface?.y ?? this.context.floor(), surface);
  }

  private feed(): void {
    const apple = this.apple();
    this.context.pose('reach');
    this.syncScene = () => {
      const from = this.point(0.72, -0.18);
      const to = this.point(0.37, -0.92);
      Object.assign(apple, arcPosition(from, to, Math.min(this.elapsed / 0.55, 1), 22));
      apple.rotation = this.elapsed * 4;
    };
    this.timeline.after(0.7, () => {
      this.context.effects.props = [];
      this.syncScene = undefined;
      this.context.pose('eat');
    });
    for (const at of [1.4, 2.4, 3.15])
      this.timeline.after(at, () => {
        const mouth = this.point(0.14, -0.79);
        this.context.effects.flecks(mouth.x, mouth.y, 'crumb');
      });
    this.timeline.after(4.05, () => {
      this.context.pose('laugh');
      this.context.say('Voilà une journée intéressante.', 1.8);
      const core = this.apple(2);
      const started = this.elapsed;
      this.syncScene = () => {
        const origin = this.point(0.2, -0.58);
        const t = this.elapsed - started;
        core.x = origin.x + this.context.hero().facing * Math.min(28, t * 22);
        core.y = Math.min(this.context.hero().y - core.size / 2, origin.y + t * t * 180);
        core.rotation = t * 3;
        core.opacity = clamp((1.35 - t) / 0.4, 0, 1);
      };
    });
    this.timeline.after(5.4, this.finish);
  }

  private juggle(): void {
    const apples = [this.apple(), this.apple(), this.apple()];
    this.context.pose('juggle');
    this.syncScene = () => {
      apples.forEach((apple, i) => {
        const phase = this.elapsed * 4 + (i * Math.PI * 2) / 3;
        Object.assign(
          apple,
          this.point(Math.sin(phase) * 0.31, -0.62 - Math.abs(Math.cos(phase)) * 0.56),
        );
        apple.rotation = phase;
        apple.opacity = clamp(this.elapsed * 3 - i * 0.25, 0, 1);
      });
    };
    const dropsApple = Math.random() < 0.35;
    if (!dropsApple) {
      this.timeline.after(4.8, () => {
        this.context.effects.props = [];
        this.syncScene = undefined;
        this.context.pose('laugh');
      });
      this.timeline.after(5.5, this.finish);
      return;
    }
    this.timeline.after(3.8, () => this.recoverApple(apples[0]!));
  }

  private recoverApple(apple: SpriteProp): void {
    this.context.effects.props = [apple];
    this.context.say('Pas celle-là !', 2.5);
    this.context.pose('reach');
    const hero = this.context.hero();
    const from = { x: apple.x, y: apple.y };
    const targetX = this.context.fitX(hero.x + hero.facing * hero.size * 0.75);
    const support = this.context.page.measureSurface(hero.surface);
    const landing =
      support && targetX > support.x + 20 && targetX < support.x + support.width - 20
        ? support
        : undefined;
    const start = this.elapsed;
    this.syncScene = () => {
      const t = this.elapsed - start;
      const ground = this.context.page.measureSurface(landing?.element)?.y ?? this.context.floor();
      apple.x = lerp(from.x, targetX, Math.min(t / 0.75, 1));
      apple.y = Math.min(ground - apple.size / 2, from.y + t * 80 + t * t * 280);
      apple.rotation = t * 5;
    };
    this.timeline.after(0.8, () => {
      const current = this.context.page.measureSurface(landing?.element);
      const motion = this.travel(targetX, current?.y ?? this.context.floor(), current, () => {
        this.syncScene = undefined;
        this.context.effects.props = [];
        this.context.pose('laugh');
        this.timeline.after(0.65, this.finish);
      });
      motion.duration = 0.55;
      motion.arc = 28;
    });
  }

  private watch(): void {
    this.context.pose('watch');
    this.syncScene = () => {
      const hero = this.context.hero();
      const head = this.point(0.08, -0.87);
      const target = this.pointer ?? {
        x: hero.x + Math.cos(this.elapsed * 2) * hero.size,
        y: head.y + Math.sin(this.elapsed * 2) * hero.size,
      };
      hero.facing = target.x >= hero.x ? 1 : -1;
      hero.look = {
        x: clamp((target.x - head.x) / hero.size, -1, 1),
        y: clamp((target.y - head.y) / hero.size, -1, 1),
      };
    };
    this.timeline.after(2.1, () => {
      this.syncScene = undefined;
      const hero = this.context.hero();
      const facing = hero.facing;
      const support = this.context.page.measureSurface(hero.surface);
      let x = this.context.fitX(hero.x - facing * hero.size * 0.2);
      if (support) x = clamp(x, support.x + 20, support.x + support.width - 20);
      const step = this.context.move(x, hero.y, false, support);
      step.duration = 0.3;
      step.onComplete = () => {
        this.context.pose('laugh');
        this.timeline.after(0.85, this.finish);
      };
      hero.facing = facing;
      this.context.pose('watch');
    });
  }

  private beg(): void {
    this.context.pose('beg');
    this.context.say('Tu n’aurais pas une pomme ?', 4);
    this.syncScene = () => {
      this.context.hero().rotation = Math.sin(this.elapsed * 3) * 0.035;
    };
    this.timeline.after(2.5, () => this.context.say('Juste une. Toute petite.', 2.5));
    this.timeline.after(5, this.finish);
  }

  private peek(): void {
    const surface = this.chooseSurface();
    this.tracked = surface?.element;
    const hide = () => {
      this.context.pose('peek');
      const started = this.elapsed;
      this.syncScene = () => {
        const hero = this.context.hero();
        const box = this.context.page.measureSurface(surface?.element);
        const top = box?.y ?? this.context.floor();
        const t = this.elapsed - started;
        const reveal = t < 0.65 ? t / 0.65 : t > 2.6 ? Math.max(0, (3.2 - t) / 0.6) : 1;
        hero.surface = box?.element;
        hero.surfaceOffsetY = hero.size * (0.39 * (1 - reveal) + 0.025);
        hero.y = top + hero.surfaceOffsetY;
        hero.occlusion = {
          left: box?.x ?? 0,
          top,
          width: box?.width ?? this.context.page.bounds.width,
          height: Math.max(box?.height ?? hero.size, hero.size),
        };
      };
      this.timeline.after(3.2, () => {
        this.syncScene = undefined;
        const hero = this.context.hero();
        hero.occlusion = undefined;
        const current = this.context.page.measureSurface(surface?.element);
        const motion = this.travel(hero.x, current?.y ?? this.context.floor(), current);
        motion.duration = 0.65;
        motion.arc = 28;
      });
    };
    if (surface) this.travel(surface.x + surface.width / 2, surface.y, surface, hide);
    else hide();
  }

  private phase(): void {
    const hero = this.context.hero();
    const surface = this.chooseSurface();
    this.tracked = surface?.element;
    const passThrough = () => {
      const box = this.context.page.measureSurface(surface?.element);
      const started = this.elapsed;
      const x = box
        ? box.x + box.width + hero.size * 0.25
        : this.context.page.bounds.width *
          (hero.x > this.context.page.bounds.width / 2 ? 0.25 : 0.75);
      const offsetY = box ? box.height * 0.5 + hero.size * 0.3 : 0;
      const crossing = this.context.move(
        this.context.fitX(x),
        box ? box.y + offsetY : this.context.floor(),
        false,
        box,
      );
      crossing.duration = 1.9;
      crossing.targetOffsetY = offsetY;
      crossing.clampToSurface = false;
      this.context.pose('phase');
      this.syncScene = () => {
        const progress = clamp((this.elapsed - started) / crossing.duration, 0, 1);
        hero.opacity = 1 - Math.sin(progress * Math.PI) * 0.8;
      };
      crossing.onComplete = () => {
        this.syncScene = undefined;
        hero.opacity = 1;
        const top = this.context.page.measureSurface(surface?.element);
        if (top) this.travel(top.x + top.width / 2, top.y, top);
        else this.finish();
      };
    };
    if (surface) {
      const offsetY = surface.height * 0.5 + hero.size * 0.3;
      const approach = this.travel(
        surface.x - hero.size * 0.25,
        surface.y + offsetY,
        surface,
        passThrough,
      );
      approach.targetOffsetY = offsetY;
      approach.clampToSurface = false;
    } else passThrough();
  }

  private notebook(): void {
    this.context.pose('write');
    const target = this.chooseSurface();
    this.timeline.after(1.2, () => {
      if (target && this.context.interacts() && !this.context.reducedMotion())
        this.context.page.haunt(target.element);
      this.context.say('Une petite note en marge…', 2.2);
    });
    this.timeline.after(2.8, () => this.context.pose('laugh'));
    this.timeline.after(3.6, this.finish);
  }
}
